import React, { useRef, useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import { Upload, CheckCircle2, FileText, Edit2, Trash2, Archive } from "lucide-react";

// Helper: Truncate text for table cells
const truncate = (str, max = 60) => str?.length > max ? str.slice(0, max) + "..." : str;

const RESOURCE_API_URL = 'https://portal.lsb.go.ke/lsb/resources/';

const initialFormState = { title: '', message: '', document: null, documentName: '', resource_id: null, existingDocumentUrl: null, existingDocumentName: '' };

const ResourceTab = () => {
    const [form, setForm] = useState(initialFormState);
    const [showModal, setShowModal] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [addMode, setAddMode] = useState(false);
    const [resources, setResources] = useState([]);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [fetchError, setFetchError] = useState('');
    const [submitError, setSubmitError] = useState('');
    const [lastRefresh, setLastRefresh] = useState(Date.now());
    const inputFileRef = useRef();

    // Fetch Resources
    const fetchResources = async () => {
        setLoading(true);
        setFetchError('');
        try {
            const accessToken = localStorage.getItem('accessToken');
            const res = await fetch(RESOURCE_API_URL, {
                method: 'GET',
                headers: {
                    'Authorization': accessToken ? `Bearer ${accessToken}` : '',
                }
            });
            if (!res.ok) throw new Error("Failed to fetch resources");
            const data = await res.json();
            const rawResources = Array.isArray(data) ? data : (data?.results || []);
            // Normalize: Ensure every item has a resource_id (fallback to id)
            const normalized = rawResources.map(n => ({
                ...n,
                resource_id: n.resource_id || n.id
            }));
            setResources(normalized);
            setLastRefresh(Date.now()); // Fix: Update lastRefresh after successful fetch
        } catch (err) {
            setFetchError(err.message || "Unable to fetch resources.");
        } finally {
            setLoading(false);
        }
    };

    // Set up auto-refresh every 15s
    useEffect(() => {
        // ERROR: Originally called fetchComplaints instead of fetchResources!
        fetchResources();
        const interval = setInterval(() => {
            fetchResources();
        }, 15000);

        return () => clearInterval(interval);
        // eslint-disable-next-line
    }, []);

    // File handler - the server expects 'document' for the upload
    const handleFileChange = e => {
        const file = e.target.files[0];
        if (file && file.type === "application/pdf") {
            setForm(f => ({
                ...f,
                document: file,
                documentName: file.name,
                // When uploading new file, remove reference to existing
                existingDocumentUrl: null,
                existingDocumentName: ''
            }));
        } else {
            setForm(f => ({ ...f, document: null, documentName: '' }));
        }
    };

    // Modal open for edit ONLY
    const openEditModal = (n) => {
        // When editing, show the currently attached document, if any
        setForm({
            title: n.title || '',
            message: n.message || '',
            document: null,
            documentName: '',
            resource_id: n.resource_id || n.id, // use fallback!
            existingDocumentUrl: n.document || null,
            existingDocumentName: n.document ? getFilenameFromUrl(n.document) : '',
        });
        setEditMode(true);
        setAddMode(false);
        setShowModal(true);
    };

    // Helper: extract filename from URL
    function getFilenameFromUrl(url) {
        try {
            if (!url) return '';
            // Try to extract everything after last /
            const u = decodeURIComponent(url.split('?')[0]); // Remove query params
            return u.substring(u.lastIndexOf('/') + 1);
        } catch {
            return '';
        }
    }

    // Modal open for "add" mode
    const openAddModal = () => {
        setForm(initialFormState);
        setAddMode(true);
        setEditMode(false);
        setShowModal(true);
    };

    // Unified handler for both create and update using PUT method
    const handlePut = async () => {
        setSubmitting(true);
        setSubmitError('');
        const formData = new FormData();
        formData.append('title', form.title);
        formData.append('message', form.message);

        // If it's an update, we use the existing resource_id. 
        // For a new entry, we post with a "new" resource_id.
        const isUpdate = editMode && form.resource_id;
        const currentResourceId = isUpdate ? form.resource_id : `RES_${Date.now()}`;

        formData.append('resource_id', currentResourceId);

        // Attachment: Only append if it's a new file.
        if (form.document) {
            formData.append('document', form.document);
        }

        const accessToken = localStorage.getItem('accessToken');

        // URL formatting: updates use the resource_id as a query parameter
        let requestUrl = RESOURCE_API_URL;
        if (isUpdate) {
            requestUrl = `${RESOURCE_API_URL}?resource_id=${encodeURIComponent(currentResourceId)}`;
        }

        try {
            const res = await fetch(requestUrl, {
                method: 'PUT',
                headers: {
                    'Authorization': accessToken ? `Bearer ${accessToken}` : '',
                },
                body: formData,
            });

            if (!res.ok) {
                let msg = isUpdate ? 'Failed to update resource.' : 'Failed to add resource.';
                try {
                    const data = await res.json();
                    if (data?.detail) msg = data.detail;
                } catch { }
                throw new Error(msg);
            }

            const resData = await res.json();
            const normalized = { ...resData, resource_id: resData.resource_id || resData.id || currentResourceId };

            if (isUpdate) {
                setResources(prev => prev.map(n => ((n.resource_id === form.resource_id || n.id === form.resource_id) ? normalized : n)));
            } else {
                setResources(prev => [normalized, ...prev]);
            }

            setShowModal(false);
            setForm(initialFormState);
            setEditMode(false);
            setAddMode(false);
            await Swal.fire({
                icon: 'success',
                title: isUpdate ? 'Resource Updated' : 'Resource Added',
                text: isUpdate ? 'The resource has been updated successfully.' : 'The resource has been added successfully.',
                timer: 2000,
                showConfirmButton: false,
            });
        } catch (err) {
            setSubmitError(err.message || (editMode ? "Error updating resource." : "Error adding resource."));
            await Swal.fire({
                icon: 'error',
                title: 'Error',
                text: err.message || (editMode ? "Error updating resource." : "Error adding resource."),
            });
        } finally {
            setSubmitting(false);
        }
    };

    // Unified Submit Handler
    const handleSubmit = e => {
        e.preventDefault();
        handlePut();
    };

    // Delete Resource
    const handleDelete = async (id) => {
        const resource_id = id;
        if (!resource_id) {
            await Swal.fire({ icon: 'error', title: 'Error', text: 'Invalid Resource ID' });
            return;
        }

        const result = await Swal.fire({
            title: 'Are you sure?',
            text: 'Do you want to delete this resource?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!',
            cancelButtonText: 'Cancel'
        });
        if (!result.isConfirmed) return;

        const accessToken = localStorage.getItem('accessToken');
        try {
            // Use resource_id as query parameter for deletion
            const res = await fetch(`${RESOURCE_API_URL}?resource_id=${encodeURIComponent(resource_id)}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': accessToken ? `Bearer ${accessToken}` : '',
                },
            });
            if (!res.ok) {
                let msg = 'Failed to delete.';
                try {
                    const data = await res.json();
                    if (data?.detail) msg = data.detail;
                } catch { }
                throw new Error(msg);
            }
            setResources(prev => prev.filter(n => (n.resource_id !== resource_id && n.id !== resource_id)));
            await Swal.fire({
                icon: 'success',
                title: 'Deleted!',
                text: 'The resource has been deleted.',
                timer: 1500,
                showConfirmButton: false,
            });
        } catch (err) {
            await Swal.fire({
                icon: 'error',
                title: 'Delete Failed',
                text: err.message || 'Failed to delete resource.',
            });
        }
    };

    return (
        <div className="max-w-7xl mx-auto">
            <div className="pb-3 flex flex-col justify-end text-xs leading-2 font-medium">
                <span>Auto-refresh: every 15s</span>
                <span className="" title={new Date(lastRefresh).toLocaleString()}>
                    Last refreshed: {new Date(lastRefresh).toLocaleTimeString()}
                </span>
            </div>

            {/* Add Resource Modal Button */}
            <div className="flex justify-end items-center my-8">
                <button
                    onClick={openAddModal}
                    className="px-5 py-2 rounded-lg bg-[#3d0707] text-white font-medium hover:bg-[#551010] transition"
                >
                    + Add Resource
                </button>
            </div>

            {/* Modal */}
            {showModal && (editMode || addMode) && (
                <div className="fixed inset-0 flex items-center justify-center z-50 bg-black/50 backdrop-blur-sm transition-all">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-0 overflow-hidden relative animate-in fade-in zoom-in duration-200">
                        <div className='bg-[#3d0707] p-6 text-white flex justify-between items-center'>
                            <h5 className="font-bold flex items-center gap-2"><Archive className="w-6 h-6" /> {addMode ? "Add Resource" : "Edit Resource"}</h5>

                            <button
                                className="text-white/80 hover:text-white transition-colors group"
                                onClick={() => {
                                    setShowModal(false);
                                    setEditMode(false);
                                    setAddMode(false);
                                    setForm(initialFormState);
                                }}
                                aria-label="Close"
                            >
                                <i className="fa-solid fa-xmark text-2xl transition-transform duration-200 group-hover:scale-125 group-hover:rotate-90" />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-2 px-8 py-2">
                            <div>
                                <label className="block mb-2 font-bold text-gray-700 text-[1vw] uppercase">Title</label>
                                <input
                                    type="text"
                                    value={form.title}
                                    onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-400 outline-none text-[1vw]"
                                    placeholder="Enter the resource title"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block mb-2 font-bold text-gray-700 text-[1vw] uppercase">Message</label>
                                <textarea
                                    value={form.message}
                                    onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
                                    rows={2}
                                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-400 outline-none text-[1vw]"
                                    placeholder="Write the resource message"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block mb-2 font-bold text-gray-700 text-[1vw] uppercase">Attachment</label>
                                {/* Display current document if not replacing during edit, else handle normal upload UI */}
                                <div
                                    onClick={() => inputFileRef.current && inputFileRef.current.click()}
                                    className={`flex flex-col items-center justify-center border-2 border-dashed rounded-xl p-6 cursor-pointer transition
                                        ${(!!form.documentName || (!!form.existingDocumentUrl && !!form.existingDocumentName && editMode && !form.document))
                                            ? "border-emerald-500 bg-emerald-50"
                                            : "border-slate-300 hover:border-indigo-400 bg-gray-50"
                                        }`}
                                >
                                    {(form.documentName)
                                        ? (
                                            <>
                                                <CheckCircle2 className="w-10 h-10 text-emerald-500 mb-2" />
                                                <span className="font-semibold">File attached</span>
                                                <span className="text-xs text-gray-500">PDF only</span>
                                                <input
                                                    type="file"
                                                    accept=".pdf"
                                                    className="hidden"
                                                    ref={inputFileRef}
                                                    onChange={handleFileChange}
                                                />
                                                <span className="mt-2 text-xs bg-emerald-100 px-2 py-1 rounded text-emerald-700 flex items-center gap-2">
                                                    <FileText className="w-4 h-4" /> {form.documentName}
                                                </span>
                                            </>
                                        )
                                        : (editMode && form.existingDocumentUrl && !form.document) ? (
                                            <>
                                                <CheckCircle2 className="w-10 h-10 text-emerald-500 mb-2" />
                                                <span className="font-semibold text-center">
                                                    Existing Attachment
                                                </span>
                                                <span className="text-xs text-gray-500">PDF only</span>
                                                <input
                                                    type="file"
                                                    accept=".pdf"
                                                    className="hidden"
                                                    ref={inputFileRef}
                                                    onChange={handleFileChange}
                                                />
                                                <span className="mt-2 text-xs bg-indigo-100 px-2 py-1 rounded text-indigo-700 flex items-center gap-2">
                                                    <FileText className="w-4 h-4" />
                                                    <a
                                                        href={form.existingDocumentUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="underline hover:text-indigo-900"
                                                        onClick={e => e.stopPropagation()}
                                                    >
                                                        {form.existingDocumentName}
                                                    </a>
                                                </span>
                                                <span className="mt-2 text-xs text-gray-500">
                                                    Click to upload a new file to replace.
                                                </span>
                                            </>
                                        ) : (
                                            <>
                                                <Upload className="w-10 h-10 text-indigo-400 mb-2" />
                                                <span className="font-semibold">
                                                    Click to Upload Attachment
                                                </span>
                                                <span className="text-xs text-gray-500">PDF only</span>
                                                <input
                                                    type="file"
                                                    accept=".pdf"
                                                    className="hidden"
                                                    ref={inputFileRef}
                                                    onChange={handleFileChange}
                                                />
                                            </>
                                        )
                                    }
                                </div>
                                {/* extra user guidance */}
                                {editMode && form.existingDocumentUrl && !form.documentName && !form.document && (
                                    <div className="text-xs text-gray-500 mt-2">
                                        Leave as is to keep the existing attachment.
                                    </div>
                                )}
                                {/* If removing previously selected replacement file */}
                                {editMode && form.documentName && (
                                    <div className="flex justify-end mt-1">
                                        <button
                                            type="button"
                                            onClick={e => {
                                                setForm(f => ({
                                                    ...f,
                                                    document: null,
                                                    documentName: '',
                                                    existingDocumentUrl: f.existingDocumentUrl,
                                                    existingDocumentName: f.existingDocumentName
                                                }));
                                                e.stopPropagation();
                                            }}
                                            className="text-xs text-red-600 underline hover:text-red-800"
                                        >
                                            Cancel new file
                                        </button>
                                    </div>
                                )}
                            </div>
                            {submitError && <p className="text-red-600">{submitError}</p>}
                            <div className="text-right">
                                <button
                                    type="submit"
                                    disabled={submitting}
                                    className="bg-[#3d0707] text-white px-6 py-2 rounded-lg font-semibold shadow hover:bg-[#551010] transition disabled:opacity-70"
                                >
                                    {submitting
                                        ? (addMode ? "Adding..." : "Updating...")
                                        : (addMode ? "Add Resource" : "Update Resource")}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <div className="">
                {loading ? (
                    <div className="text-center text-gray-800 py-10">
                        <svg className="animate-spin h-9 w-9 mx-auto text-indigo-500 mb-3" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                        </svg>
                        Loading resources...
                    </div>
                ) : fetchError ? (
                    <div className="text-red-500 text-center py-4">{fetchError}</div>
                ) : (
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                        <div className="overflow-x-auto">
                            <div className="max-h-[60vh] overflow-y-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr className="bg-gray-100 uppercase">
                                            <th className="py-2 px-4 text-center text-sm font-bold text-gray-500 uppercase tracking-wider">ID</th>
                                            <th className="py-2 px-4 text-left text-sm font-bold text-gray-500 uppercase tracking-wider">Title</th>
                                            <th className="py-2 px-4 text-center text-sm font-bold text-gray-500 uppercase tracking-wider">Attachment</th>
                                            <th className="py-2 px-4 text-center text-sm font-bold text-gray-500 uppercase tracking-wider">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {resources.length === 0 ? (
                                            <tr>
                                                <td colSpan={5} className="py-10 text-center">
                                                    No resources found.
                                                </td>
                                            </tr>
                                        ) : (
                                            resources.map(n => (
                                                <tr key={n.resource_id || n.id} className="hover:bg-slate-50 border-t">
                                                    <td className="py-2 px-4 whitespace-nowrap">
                                                        <span className="font-mono text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded">
                                                            {truncate(String(n.resource_id || n.id), 8)}
                                                        </span>
                                                    </td>
                                                    <td className="py-2 px-4 font-medium">{truncate(n.title, 30)}</td>
                                                    <td className="py-2 px-4 text-center">
                                                        {n.document
                                                            ? (
                                                                <a
                                                                    href={n.document}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="px-4 py-1.5 bg-indigo-50 text-indigo-600 rounded-lg text-sm font-bold hover:bg-indigo-100 transition shadow-sm text-decoration-none"
                                                                >View</a>
                                                            )
                                                            : <span className="text-gray-400 text-xs">None</span>
                                                        }
                                                    </td>
                                                    <td className="py-2 px-4 text-center">
                                                        <div className="flex justify-center gap-2">
                                                            {/* Edit action */}
                                                            <button
                                                                title="Edit"
                                                                className="p-2 text-green-600 hover:bg-indigo-50 rounded-lg transition"
                                                                onClick={() => openEditModal(n)}
                                                            >
                                                                <Edit2 className="w-4 h-4" />
                                                            </button>

                                                            {/* Delete action */}
                                                            <button
                                                                title="Delete"
                                                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                                                                onClick={() => handleDelete(n.resource_id)}
                                                            >
                                                                <Trash2 className="w-4 h-4" />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ResourceTab;
